import { Box, CircularProgress, styled, Typography } from '@mui/material';
import { isEmpty } from 'lodash';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { OptionButton, OptionButtonType } from '../../../../components/Button/OptionButton';
import { ResponsiveInfinityScroll } from '../../../../components/ResponsiveInfinityScroll';
import SearchBar from '../../../../components/SearchBar';
import { dimensions } from '../../../../styles/dimensions';
import { theme } from '../../../../styles/theme';
import { SideBarItemType } from '../../../../types/route';
import { $get } from '../../../../utils/http';
import { Processor } from '../../../../models/processor';

type Props = {
  id: string;
};

const ProcessorList: React.FunctionComponent<Props> = ({ id }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [processors, setProcessors] = useState<Processor[]>([]);
  const [filteredProcessors, setFilteredProcessors] = useState<Processor[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [keyword, setKeyword] = useState<string>('');

  const fetchProcessors = async () => {
    setLoading(true);
    try {
      const response = await $get('processor/all');
      if (response && response.Processors) {
        setProcessors(response.Processors);
        setFilteredProcessors(response.Processors);
      }
    } catch (error) {
      console.error('Error fetching processors:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProcessors();
  }, []);

  const handleSearch = (value: string) => {
    setKeyword(value);
    if (value.trim() === '') {
      setFilteredProcessors(processors);
    } else {
      const filtered = processors.filter(processor => processor.proName.toLowerCase().includes(value.toLowerCase()));
      setFilteredProcessors(filtered);
    }
  };

  const handleChooseProcessor = (processorId: number) => {
    navigate(`/${SideBarItemType.OrganisationSettings}/processor/${processorId}`);
  };

  const handleAddNew = () => {
    navigate(`/${SideBarItemType.OrganisationSettings}/processor/add-new`);
  };

  // Function to handle scrolling - not needed but prevents linter warning
  const handleScrollDown = () => {
    // No implementation needed as we're not paginating
    console.log('Scroll event triggered');
  };

  return (
    <ListWrapper>
      <Header>
        <Typography variant="subtitle4" textTransform="uppercase" color="grey.100">
          {t('processor.list')} ({processors.length})
        </Typography>

        <OptionButton
          type={OptionButtonType.ADD_NEW}
          content={t('add_new')}
          isscaledown={true}
          onClick={handleAddNew}
        />
      </Header>

      <SearchBar
        onClear={() => setFilteredProcessors(processors)}
        onSearch={handleSearch}
        placeHolder={t('processor.search_placeholder')}
      />

      {loading ? (
        <Box display="flex" justifyContent="center" mt={2}>
          <CircularProgress color="primary" size={40} />
        </Box>
      ) : !isEmpty(filteredProcessors) ? (
        <ResponsiveInfinityScroll
          height="40vh"
          dataLength={filteredProcessors.length}
          next={handleScrollDown}
          hasMore={false}
          loader={null}>
          {filteredProcessors.map(processor => (
            <Item
              variant="body1"
              key={processor.proId}
              className={id === processor.proId.toString() ? 'active' : ''}
              onClick={() => handleChooseProcessor(processor.proId)}>
              {processor.proName}
            </Item>
          ))}
        </ResponsiveInfinityScroll>
      ) : (
        <Typography variant="subtitle1" color="grey.200" mt={2}>
          {t('search_no_result')}
        </Typography>
      )}
    </ListWrapper>
  );
};

export default ProcessorList;

const { grey, primary, common } = theme.palette;
const { baseMD, baseXS, baseSM, baseLG } = dimensions;

const Header = styled(Box)`
  width: 100%;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid ${grey[400]};
`;

const Item = styled(Typography)`
  display: flex;
  align-items: center;
  padding: 0 0 0 3%;
  width: 96%;
  line-height: 40px;
  border-radius: 2px;
  cursor: pointer;
  &.active {
    background-color: ${primary.main};
    color: ${common.white};
    font-weight: 700;
  }
`;

const ListWrapper = styled(Box)`
  width: 300px;
  padding: ${baseSM} ${baseLG};
  border-radius: ${baseXS};
  height: calc(100% - ${baseMD});
  box-shadow: ${theme.shadows[2]};
  background-color: ${theme.palette.grey[500]};

  ${props => props.theme.breakpoints.down('lg')} {
    width: 200px;
  }
`;
